import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Thumb } from "./Thumb";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/ui";
import { haptic } from "../../telegram/haptics";
import { listItem, pressRow } from "../../motion";
import { formatGrams, isLatinScript } from "../../i18n/format";
import { useT } from "../../i18n/strings";
import { kindLabels, labelOf, ownershipLabels, purityLabel } from "../../i18n/choices";
import type { PieceRow as Row } from "../../api/types";
import s from "./pieces.module.css";

/**
 * Kind, purity and weight: the three things a tray label says. The kind never
 * truncates; the figures give way first. Only the digits run LTR, so the unit
 * lands on the reading side in both scripts.
 */
export function PieceMeta({ piece, compact }: { piece: Row; compact?: boolean }) {
  const t = useT();
  const weight = piece.net_weight_g ?? piece.gross_weight_g;
  const purity = purityLabel(piece.purity_permille, piece.metal);
  return (
    <>
      <span className={`${s.kind} bidi`}>{labelOf(kindLabels, piece.kind)}</span>
      <span className={s.figures}>
        {!compact && purity && <span className="mono">{purity}</span>}
        {weight !== null && (
          <span>
            <span className="mono">{formatGrams(weight)}</span> {t.form.gram}
          </span>
        )}
      </span>
    </>
  );
}

export function PieceRow({ piece, onMediaError }: { piece: Row; onMediaError?: () => void }) {
  const t = useT();
  const latin = isLatinScript(piece.title);
  const status = labelOf(ownershipLabels, piece.ownership_status);

  return (
    <motion.div variants={listItem} exit="exit" layout="position">
      <motion.div {...pressRow}>
        <Link
          to={`/piece/${piece.id}`}
          className={`${s.row} paper hoverable`}
          onClick={() => haptic.tap()}
        >
          <Thumb
            media={piece.primary_media}
            layoutId={piece.primary_media ? `hero-${piece.primary_media.id}` : undefined}
            onMediaError={onMediaError}
          />

          <div className={s.rowBody}>
            <span className={[s.title, latin && s.titleLatin, "bidi"].filter(Boolean).join(" ")}>
              {piece.title || t.detail.untitled}
            </span>
            <span className={s.meta}>
              <PieceMeta piece={piece} />
            </span>
          </div>

          <div className={s.rowTail}>
            {status && <span className={s.typeChip}>{status}</span>}
            <Icon name="forward" size={16} />
          </div>
        </Link>
      </motion.div>
    </motion.div>
  );
}

export function PieceRowSkeleton() {
  return (
    <div className={s.skeletonRow}>
      <Skeleton width={72} height={72} radius="var(--radius-chip)" />
      <div className={s.skeletonBody}>
        <Skeleton height={14} width="55%" radius="4px" />
        <Skeleton height={12} width="35%" radius="4px" />
      </div>
    </div>
  );
}
